import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, ArrowUpRight } from "lucide-react";
import { RevealText } from "./Reveal";
import { Starfield } from "./Starfield";
import { scrollToId } from "@/lib/scroll";

export const Hero = ({ settings }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "35%"]);
  const bgScale = useTransform(scrollYProgress, [0, 1], [1.1, 1.28]);
  const contentY = useTransform(scrollYProgress, [0, 1], ["0%", "60%"]);
  const contentOpacity = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  const hero = settings?.heroImage;

  return (
    <section id="hero" ref={ref} className="relative min-h-screen overflow-hidden" data-testid="hero-section">
      <motion.div style={{ y: bgY, scale: bgScale }} className="absolute inset-0">
        <img src={hero} alt="Deep space resonance field" className="h-full w-full object-cover" />
        <div className="absolute inset-0 bg-[#05060A]/75" />
        <div className="absolute inset-0 bg-gradient-to-b from-[#05060A]/40 via-transparent to-[#05060A]" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_35%,rgba(29,233,182,0.12),transparent_55%)]" />
      </motion.div>

      <Starfield count={70} />

      <motion.div
        style={{ y: contentY, opacity: contentOpacity }}
        className="section-shell relative z-10 flex min-h-screen flex-col justify-center pt-24"
      >
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3, duration: 1 }}
          className="overline mb-6"
          data-testid="hero-overline"
        >
          Consciousness · Resonance · Ancient Technology
        </motion.p>

        <h1 className="font-display font-light tracking-tighter text-white text-5xl md:text-7xl lg:text-8xl leading-[0.95]" data-testid="hero-title">
          <RevealText
            lines={["Resonance is the", "memory of stone.", "Coherence is the", "song of the field."]}
            lineClassName=""
          />
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.1, duration: 1 }}
          className="mt-8 max-w-xl font-sans text-base md:text-lg font-light leading-relaxed text-slate-300"
          data-testid="hero-tagline"
        >
          {settings?.tagline}
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.35, duration: 1 }}
          className="mt-10 flex flex-wrap items-center gap-5"
        >
          <button onClick={() => scrollToId("research")} className="btn-plasma" data-testid="hero-enter-research">
            Enter the Research <ArrowDown className="h-4 w-4" strokeWidth={1.5} />
          </button>
          <a href={settings?.socialLinks?.substack} target="_blank" rel="noreferrer" className="btn-gold" data-testid="hero-substack">
            Subscribe on Substack <ArrowUpRight className="h-3.5 w-3.5" strokeWidth={1.5} />
          </a>
        </motion.div>
      </motion.div>

      <div className="pointer-events-none absolute bottom-8 left-1/2 z-10 -translate-x-1/2">
        <motion.div animate={{ y: [0, 10, 0] }} transition={{ repeat: Infinity, duration: 2.5 }}>
          <ArrowDown className="h-5 w-5 text-plasma-teal/60" strokeWidth={1.5} />
        </motion.div>
      </div>
    </section>
  );
};
